/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComposedChart, Line, ResponsiveContainer, XAxis } from 'recharts';
import { Bar, CartesianGrid, Legend, Tooltip, YAxis } from 'recharts';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { format } from 'date-fns';
import { useState } from 'react';
import { Label, LabeledControl, Switch } from '@zillow/constellation-10';
import { formatLocalDate } from '../../../../../utils/localDateToDate';
import { GraphWrapper, type GraphWrapperInfoCard } from '../../../../SharedComponents/GraphWrapper';
import { MonthAndYearCustomTick } from '../../../../SharedComponents/MonthAndYearCustomTick';
import { CustomGraphLegend } from '../../../../SharedComponents/CustomGraphLegend';
import { StackedBarShape } from '../../../../SharedComponents/StackedBarShape';
import { CustomGraphTooltip } from '../../../../SharedComponents/CustomGraphTooltip';
import { formatStackedNumbers } from '../../../../../utils';
import { isAllSingleFamily } from '../../../../../utils/isAllSingleFamily';
import { roundUp } from '../../../../../utils/roundUp';
import { BuildingType, DataType } from '../OverviewReport.types';
import { getGraphTitle } from '../../../../../utils/getGraphTitle';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitOverviewProspectsMarketAverageEvent } from '../../../../../analytics/events';
import type { ProspectsChartData, ProspectsPackageBreakdownResult } from '../OverviewReport.data';

const lightenColor = (hex: string, amount = 0.4): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lighten = (c: number) => Math.round(c + (255 - c) * amount);
  return `#${lighten(r).toString(16).padStart(2, '0')}${lighten(g).toString(16).padStart(2, '0')}${lighten(b).toString(16).padStart(2, '0')}`;
};

const stripePatternId = (key: string) => `stripe-pkg-${key.replace(/[^a-zA-Z0-9]/g, '-')}`;

type ProspectsProps = {
  selectedRegionId: number;
  chartData: Array<ProspectsChartData>;
  isRegionPerformanceLoading: boolean;
  buildingTypeFilter: BuildingType;
  setBuildingTypeFilter: (value: BuildingType) => void;
  displayType: DataType;
  setDisplayType: (value: DataType) => void;
  packageBreakdownData?: ProspectsPackageBreakdownResult;
};

export const Prospects = ({
  selectedRegionId,
  chartData,
  isRegionPerformanceLoading,
  buildingTypeFilter,
  setBuildingTypeFilter,
  displayType,
  setDisplayType,
  packageBreakdownData,
}: ProspectsProps) => {
  const { user } = useRentalManagerUser();
  const [showPackageBreakdown, setShowPackageBreakdown] = useState(false);

  const { filteredProperties, regionsMapping, selectedFilters, isFetchingInitialData, analyticsLastUpdatedDates } =
    useMainLayout();
  const lastUpdatedDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioPerformanceLastUpdated);
  const marketDataUpdatedDate = formatLocalDate(analyticsLastUpdatedDates?.marketRentLastUpdated);
  const currentDateFormatted = format(new Date(), 'MMM yyyy');

  const isAnyPropertySingleFamily = filteredProperties.some((property) => property.aggregateStats.isSingleFamily);
  const allSingleFamily = isAllSingleFamily(filteredProperties);
  const effectiveBuildingType = allSingleFamily ? BuildingType.SINGLE_FAMILY : buildingTypeFilter;

  const title = getGraphTitle({
    graphName: 'Listing prospects',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });
  const isLoading = isFetchingInitialData || isRegionPerformanceLoading;
  const mtdData = chartData.find((data) => data.name === currentDateFormatted);
  const mtdDirectLeads = mtdData?.['Month to date direct leads'] || 0;
  const mtdWebReferrals = mtdData?.['Month to date listing web referrals'] || 0;

  const buildingTypeText = effectiveBuildingType === BuildingType.SINGLE_FAMILY ? 'single-family' : 'multifamily';

  let firstCard: GraphWrapperInfoCard = {
    title: 'Total leads MTD',
    infoText:
      'Total :buildingTypeText phone and email leads for your portfolio or property in the selected market, month to date.'.replace(
        ':buildingTypeText',
        buildingTypeText,
      ),
    value: mtdDirectLeads,
  };
  let secondCard: GraphWrapperInfoCard = {
    title: 'Total listing prospects MTD',
    infoText:
      'Total :buildingTypeText listing prospects (phone leads, email leads, and listing web referrals) for your portfolio or property in the selected market, month to date.'.replace(
        ':buildingTypeText',
        buildingTypeText,
      ),
    value: mtdDirectLeads + mtdWebReferrals,
  };

  if (displayType === DataType.MARKET_AVERAGE) {
    firstCard = {
      title: 'Portfolio average',
      infoText:
        'Average :buildingTypeText listing prospects (leads and listing web referrals) for your portfolio in the selected market, month to date.'.replace(
          ':buildingTypeText',
          buildingTypeText,
        ),
      value: roundUp(
        chartData.reduce((acc, curr) => acc + curr['Avg. direct leads'] + curr['Avg. listing web referrals'], 0) /
          (chartData.length || 1),
        0,
      ),
    };
    secondCard = {
      title: 'Market average',
      infoText:
        'Average :buildingTypeText listing prospects (leads and listing web referrals) for the selected market, month to date.'.replace(
          ':buildingTypeText',
          buildingTypeText,
        ),
      value: roundUp(
        chartData.reduce((acc, curr) => acc + (curr['Median market listing prospects'] || 0), 0) /
          (chartData.length > 1 ? chartData.length - 1 : 1),
        0,
      ),
    };
  }

  const lastUpdatedText =
    lastUpdatedDate && marketDataUpdatedDate
      ? ` Portfolio data updated ${lastUpdatedDate}, market data updated ${marketDataUpdatedDate}`
      : ``;

  const isBreakdownActive = showPackageBreakdown && displayType === DataType.PORTFOLIO_TOTAL && packageBreakdownData;
  const breakdownPackages = packageBreakdownData?.packages ?? [];
  const canShowToggle = displayType === DataType.PORTFOLIO_TOTAL && breakdownPackages.length > 0;

  const toggleControl = canShowToggle ? (
    <LabeledControl
      control={<Switch checked={showPackageBreakdown} onChange={() => setShowPackageBreakdown((prev) => !prev)} />}
      label={<Label>Show package breakdown</Label>}
    />
  ) : undefined;

  const renderBreakdownChart = () => {
    if (!packageBreakdownData) return null;
    const { chartData: breakdownChartData, packages } = packageBreakdownData;

    return (
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={breakdownChartData}>
          <defs>
            {packages.map((pkg) => (
              <pattern
                key={`stripe-${pkg.displayName}`}
                id={stripePatternId(pkg.mtdProspectsKey)}
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" fill={lightenColor(pkg.color, 0.3)} />
                <rect width="3" height="6" fill={pkg.color} />
              </pattern>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" interval={0} tick={<MonthAndYearCustomTick />} />
          <YAxis tickFormatter={(value) => formatStackedNumbers(value)} />
          <Tooltip
            content={
              <CustomGraphTooltip
                displayTotal
                filterPayload={(filteredPayload: Array<any>, displayLabel: string, allPayload: Array<any>) => {
                  const isCurrentMonth = displayLabel === currentDateFormatted;
                  const dataPoint = allPayload[0]?.payload;
                  if (!dataPoint) return [];
                  return packages
                    .map((pkg) => {
                      const propertiesKey = isCurrentMonth ? pkg.mtdPropertiesKey : pkg.propertiesKey;
                      const leadsKey = isCurrentMonth ? pkg.mtdLeadsKey : pkg.leadsKey;
                      const propertyCount = dataPoint[propertiesKey] as number;
                      const leadsCount = dataPoint[leadsKey] as number;
                      return propertyCount
                        ? {
                            name: `${pkg.displayName} (${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'})`,
                            value: leadsCount || 0,
                            color: pkg.color,
                          }
                        : null;
                    })
                    .filter(Boolean);
                }}
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                filterPayload={() =>
                  packages.map((pkg) => ({
                    value: pkg.displayName,
                    type: 'rect',
                    color: pkg.color,
                  }))
                }
              />
            }
          />
          {packages.flatMap((pkg) => [
            <Bar
              key={pkg.prospectsKey}
              dataKey={pkg.prospectsKey}
              stackId="a"
              fill={pkg.color}
              shape={StackedBarShape}
            />,
            <Bar
              key={pkg.mtdProspectsKey}
              dataKey={pkg.mtdProspectsKey}
              stackId="a"
              fill={`url(#${stripePatternId(pkg.mtdProspectsKey)})`}
              shape={StackedBarShape}
            />,
          ])}
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  const renderDefaultChart = () => (
    <ResponsiveContainer width="100%" height={350}>
      <ComposedChart data={chartData}>
        <defs>
          <pattern
            id="stripe-direct-leads"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill="#B88DFC" />
            <rect width="3" height="6" fill="#891EFA" />
          </pattern>
          <pattern
            id="stripe-web-referrals"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill="#E3BFFF" />
            <rect width="3" height="6" fill="#C67EFF" />
          </pattern>
          <pattern
            id="stripe-avg-direct-leads"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill="#73D4E4" />
            <rect width="3" height="6" fill="#009AAE" />
          </pattern>
          <pattern
            id="stripe-avg-web-referrals"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill="#C7F5FF" />
            <rect width="3" height="6" fill="#73E4F9" />
          </pattern>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="timestamp" interval={0} tick={<MonthAndYearCustomTick />} />
        <YAxis tickFormatter={(value) => formatStackedNumbers(value)} />
        <Tooltip
          content={
            <CustomGraphTooltip
              filterPayload={(filteredPayload: Array<any>, displayLabel: string) => {
                const isCurrentMonth = displayLabel === currentDateFormatted;
                if (displayType === DataType.PORTFOLIO_TOTAL) {
                  if (isCurrentMonth) {
                    const filtered = filteredPayload.filter(
                      (entry: any) =>
                        entry.name === 'Month to date direct leads' ||
                        entry.name === 'Month to date listing web referrals',
                    );
                    const totalValue = filtered.reduce((sum: number, e: any) => sum + (e.value || 0), 0);
                    filtered.push({
                      name: 'Total listing prospects',
                      value: totalValue,
                      color: '#891EFA',
                      splitColors: ['#891EFA', '#C67EFF'],
                    });
                    return filtered;
                  } else {
                    const filtered = filteredPayload.filter(
                      (entry: any) =>
                        entry.name === 'Total direct leads' || entry.name === 'Total listing web referrals',
                    );
                    const totalValue = filtered.reduce((sum: number, e: any) => sum + (e.value || 0), 0);
                    filtered.push({
                      name: 'Total listing prospects',
                      value: totalValue,
                      color: '#891EFA',
                      splitColors: ['#891EFA', '#C67EFF'],
                    });
                    return filtered;
                  }
                } else {
                  if (isCurrentMonth) {
                    const filtered = filteredPayload.filter(
                      (entry: any) =>
                        entry.name === 'Month to date avg. direct leads' ||
                        entry.name === 'Month to date avg. listing web referrals',
                    );
                    const totalValue = filtered.reduce((sum: number, e: any) => sum + (e.value || 0), 0);
                    filtered.push({
                      name: 'Avg. listing prospects',
                      value: totalValue,
                      color: '#009AAE',
                      splitColors: ['#009AAE', '#73E4F9'],
                    });
                    return filtered;
                  } else {
                    const filtered = filteredPayload.filter(
                      (entry: any) =>
                        entry.name === 'Avg. direct leads' ||
                        entry.name === 'Avg. listing web referrals' ||
                        entry.name === 'Median market listing prospects',
                    );
                    const avgDirectLeads = filtered.find((e: any) => e.name === 'Avg. direct leads');
                    const avgWebReferrals = filtered.find((e: any) => e.name === 'Avg. listing web referrals');
                    const totalValue = (avgDirectLeads?.value || 0) + (avgWebReferrals?.value || 0);
                    filtered.push({
                      name: 'Avg. listing prospects',
                      value: totalValue,
                      color: '#009AAE',
                      splitColors: ['#009AAE', '#73E4F9'],
                    });
                    return filtered;
                  }
                }
              }}
            />
          }
        />
        <Legend
          wrapperStyle={{ marginBottom: '-32px' }}
          content={
            <CustomGraphLegend
              labelTypes={
                displayType === DataType.PORTFOLIO_TOTAL
                  ? {
                      'Month to date': 'striped',
                      'Median market listing prospects': 'line',
                    }
                  : {
                      'Month to date': 'striped-teal',
                      'Median market listing prospects': 'line',
                    }
              }
              filterPayload={(payload: Array<any>) => {
                if (displayType === DataType.PORTFOLIO_TOTAL) {
                  const filtered = payload.filter(
                    (entry: any) =>
                      entry.value !== 'Month to date direct leads' &&
                      entry.value !== 'Month to date listing web referrals',
                  );
                  const hasMTD = payload.some(
                    (entry: any) =>
                      entry.value === 'Month to date direct leads' ||
                      entry.value === 'Month to date listing web referrals',
                  );
                  if (hasMTD) {
                    filtered.push({
                      value: 'Month to date',
                      type: 'rect',
                      color: '#891EFA',
                    });
                  }
                  return filtered;
                } else {
                  const filtered = payload.filter(
                    (entry: any) =>
                      entry.value !== 'Month to date avg. direct leads' &&
                      entry.value !== 'Month to date avg. listing web referrals' &&
                      entry.value !== 'Median market listing prospects month to date',
                  );
                  const hasMTD = payload.some(
                    (entry: any) =>
                      entry.value === 'Month to date avg. direct leads' ||
                      entry.value === 'Month to date avg. listing web referrals',
                  );
                  if (hasMTD) {
                    const marketAvgIndex = filtered.findIndex(
                      (entry: any) => entry.value === 'Median market listing prospects',
                    );
                    const mtdEntry = {
                      value: 'Month to date',
                      type: 'rect',
                      color: '#009AAE',
                    };
                    if (marketAvgIndex >= 0) {
                      filtered.splice(marketAvgIndex, 0, mtdEntry);
                    } else {
                      filtered.push(mtdEntry);
                    }
                  }
                  return filtered;
                }
              }}
            />
          }
        />
        {displayType === DataType.PORTFOLIO_TOTAL ? (
          <>
            <Bar dataKey="Total direct leads" stackId="a" fill="#891EFA" shape={StackedBarShape} />
            <Bar dataKey="Total listing web referrals" stackId="a" fill="#C67EFF" shape={StackedBarShape} />
            <Bar
              dataKey="Month to date direct leads"
              stackId="a"
              fill="url(#stripe-direct-leads)"
              shape={StackedBarShape}
            />
            <Bar
              dataKey="Month to date listing web referrals"
              stackId="a"
              fill="url(#stripe-web-referrals)"
              shape={StackedBarShape}
            />
          </>
        ) : (
          <>
            <Bar dataKey="Avg. direct leads" stackId="a" fill="#009AAE" shape={StackedBarShape} />
            <Bar dataKey="Avg. listing web referrals" stackId="a" fill="#73E4F9" shape={StackedBarShape} />
            <Bar
              dataKey="Month to date avg. direct leads"
              stackId="a"
              fill="url(#stripe-avg-direct-leads)"
              shape={StackedBarShape}
            />
            <Bar
              dataKey="Month to date avg. listing web referrals"
              stackId="a"
              fill="url(#stripe-avg-web-referrals)"
              shape={StackedBarShape}
            />

            <Line type="linear" dataKey="Median market listing prospects" stroke="#111116" strokeWidth={3} />
          </>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <GraphWrapper
      graphId="prospects"
      title={title}
      subtitle={`Listing prospects are the combined total of your phone leads, email leads, and website referrals from your listings.${lastUpdatedText}`}
      radioButtonFilter={displayType}
      setRadioButtonFilter={(value: DataType) => {
        if (user) emitOverviewProspectsMarketAverageEvent({ user });
        setDisplayType(value);
      }}
      buttonGroupFilter={isAnyPropertySingleFamily && !allSingleFamily ? buildingTypeFilter : undefined}
      setButtonGroupFilter={isAnyPropertySingleFamily && !allSingleFamily ? setBuildingTypeFilter : undefined}
      infoCards={displayType === DataType.PORTFOLIO_TOTAL ? [firstCard, secondCard] : []}
      bottomText={` Your portfolio's ${displayType === DataType.PORTFOLIO_TOTAL ? 'total' : 'average'} listing prospects — direct leads and listing web referrals — compared with the market average.`}
      loading={isLoading}
      toggleControl={toggleControl}
    >
      {isBreakdownActive ? renderBreakdownChart() : renderDefaultChart()}
    </GraphWrapper>
  );
};
